import React, {useEffect, useState} from 'react';
import {v4 as uuidv4} from 'uuid';
import debounce from 'lodash.debounce';
import { useTranslation } from 'react-i18next';

import {BACKEND_URL, INFO_URL} from '../../App.config';
import {Button, Input, Modal} from '../../ui';

import {Contact, Field, Props} from './ContactModal.types';

const getFields = (t: any): Field[] => [
    { name: 'name', placeholder: t('contactModal.fields.name.placeholder'), type: 'text' },
    { name: 'phone', placeholder: t('contactModal.fields.phone.placeholder'), type: 'tel', regExp: /[+0-9]/, error: t('contactModal.fields.phone.error') },
    { name: 'email', placeholder: t('contactModal.fields.email.placeholder'), type: 'email' },
    { name: 'companyName', placeholder: t('contactModal.fields.companyName.placeholder'), type: 'text', regExp: /^[а-яА-ЯёЁa-zA-Z ]+$/, error: t('contactModal.fields.companyName.error') },
    { name: 'country', placeholder: t('contactModal.fields.country.placeholder'), type: 'text', regExp: /^[а-яА-ЯёЁa-zA-Z]+$/, error: t('contactModal.fields.country.error') },
    { name: 'position', placeholder: t('contactModal.fields.position.placeholder'), type: 'text', regExp: /^[а-яА-ЯёЁa-zA-Z ]+$/, error: t('contactModal.fields.position.error') },
];

// Function to send form data to the backend
const sendFormData = async (formData: any) => {
    try {
        const response = await fetch(`${BACKEND_URL}/form`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formData),
        });

        if (!response.ok) {
            throw new Error('Failed to submit form data');
        }

        return await response.json();
    } catch (error) {
        console.error('Error submitting form data:', error);
    }
};

export const ContactModal: React.FC<Props> = ({ isOpen, onClose }) => {
    const { t } = useTranslation();
    const fields = getFields(t);
    
    const [contact, setContact] = useState<Contact>({
        companyName: undefined,
        country: undefined,
        email: undefined,
        name: undefined,
        phone: undefined,
        position: undefined,
    });

    // State to store the user's IP address
    const [ipAddress, setIpAddress] = useState<string | null>(null);

    // Generate a unique session ID for this user
    const [sessionId] = useState<string>(uuidv4());

    // Fetch IP address from httpbin.org/ip when component mounts
    useEffect(() => {
        const fetchIpAddress = async () => {
            try {
                const response = await fetch('https://httpbin.org/ip');

                if (!response.ok) {
                    throw new Error('Failed to fetch IP address');
                }
                const data = await response.json();
                setIpAddress(data.origin);
            } catch (error) {
                console.error('Error fetching IP address:', error);
            }
        };

        fetchIpAddress();
    }, []);

    // Function to map frontend field names to backend field names
    const mapToBackendFormat = (contactData: Contact, buttonPressed: boolean = false) => {
        return {
            name: contactData.name,
            phone: contactData.phone,
            email: contactData.email,
            company: contactData.companyName, // Map companyName to company
            country: contactData.country,
            position: contactData.position,
            button_pressed: buttonPressed, // Add button_pressed field
            ip_address: ipAddress, // Add the IP address from httpbin.org
            session_id: sessionId, // Add the unique session ID
        };
    };

    const handleSendForm =  debounce((contact: Contact) => sendFormData(mapToBackendFormat(contact, false)), 500)

    const handleInputChange = (key: string, value: string | undefined) => {
        setContact((prevState) => {
            const newContact: Contact = {...prevState, [key]: value};
            handleSendForm(newContact);

            return newContact;
        });

    };

    // Function to handle the demo access button click
    const handleDemoButtonClick = async () => {
        // Only submit if at least one field has a value
        const hasValue = Object.values(contact).some(value => value !== undefined && value !== '');

        if (hasValue) {
            // Create data with button_pressed set to true
            const backendData = mapToBackendFormat(contact, true);

            // Send the form data
            await sendFormData(backendData);
        }

        // Redirect to INFO_URL after form submission
        window.open(INFO_URL, '_self');
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={t('contactModal.title')}
            footer={<Button onClick={handleDemoButtonClick} full>{t('contactModal.button')}</Button>}
        >
            {fields.map(({ name, regExp, error, ...props }) => {
                const value = contact[name] || '';

                return (
                    <Input
                        id={name}
                        key={name}
                        value={contact[name]}
                        autoComplete="on"
                        onChange={(value) => handleInputChange(name, value)}
                        error={value?.length && !regExp?.test(value) ? error : ''}
                        {...props}
                    />
                )}
            )}
        </Modal>
    )
};
